import React, { FC } from 'react';

import { Button, Result, Row, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { OnboardingState } from '@app/models/users';
import { AuthorizationFormLayout } from '@layouts/AuthorizationFormLayout';

import { ConfirmInvitation } from './components/ConfirmInvitation';
import { InvitationCard } from './components/InvitationCard';
import { RegisterForm } from './components/RegisterForm';
import { useCounterpartyInvitationQuery } from './hooks';

export const CounterpartyInvite: FC = () => {
  const { uid = '' } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const pageT = useTranslation(NSKeys.counterpartyInvite);
  const commonT = useTranslation(NSKeys.common);

  const onboarding = useCounterpartyInvitationQuery(uid);

  if (onboarding.isInitialLoading) {
    return (
      <AuthorizationFormLayout title={pageT.t('title')}>
        <Row justify="center" style={{ marginTop: 30 }}>
          <Spin />
        </Row>
      </AuthorizationFormLayout>
    );
  }

  if (!onboarding.data) {
    return (
      <AuthorizationFormLayout title={pageT.t('title')}>
        <Result
          status="error"
          subTitle={commonT.t('error400')}
          extra={
            <Button type="primary" onClick={() => onboarding.refetch()}>
              {commonT.t('buttonReload')}
            </Button>
          }
        />
      </AuthorizationFormLayout>
    );
  }

  const { state, invitation } = onboarding.data;
  const refetch = () => onboarding.refetch();

  const renderBranch = () => {
    if (invitation && (state === OnboardingState.CreateSpace || state === OnboardingState.JoinSpace)) {
      return <RegisterForm invitationUid={uid} state={state} onStale={refetch} />;
    }
    if (invitation && state === OnboardingState.Confirm) {
      return <ConfirmInvitation invitationUid={uid} email={invitation.email} onStale={refetch} />;
    }
    return (
      <Row justify="center">
        <Button type="primary" onClick={() => navigate('/login')}>
          {pageT.t('goToLogin')}
        </Button>
      </Row>
    );
  };

  return (
    <AuthorizationFormLayout
      title={pageT.t(`state.${state}.title` as const)}
      description={pageT.t(`state.${state}.description` as const)}
    >
      <Spin spinning={onboarding.isFetching}>
        {invitation && <InvitationCard invitation={invitation} />}
        {renderBranch()}
      </Spin>
    </AuthorizationFormLayout>
  );
};
